import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import PhotoThumbnail from "../components/PhotoThumbnail";
import CameraCapture from "../components/CameraCapture";
import { submitVisitorRegistration, fetchHostsFromDB } from "../db/visitorregistrationApi";
import { fetchTenantsFromDB } from "../db/fetchTenantsFromDB";
import { saveVisitorImageWithMetadata } from "../db/saveVisitorImageWithMetadata";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getFacilitiesByTenant } from "../db/facilityApi";

const VisitorRegistration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    visitorName: "",
    visitorEmail: "",
    visitorMobileNo: "",
    visitReason: "",
    tenantId: "",
    hostUid: "",
    visitorTime: new Date(),
    photoUrl: "",
  });

  const [tenants, setTenants] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [photoWarningOpen, setPhotoWarningOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const tenantsData = await fetchTenantsFromDB();
        setTenants(tenantsData);
      } catch {
        setError("Failed to load tenants");
      }
    };
    loadTenants();
  }, []);

  const selectedTenant = tenants.find(
    (t) => (t.tenantId || t.id) === formData.tenantId
  );

  const handleDateChange = (newValue) => {
    setFormData((prev) => ({ ...prev, visitorTime: newValue }));
  };

  // Set photoUrl directly from CameraCapture (download URL)
  const handlePhotoCapture = (downloadURL) => {
    setFormData((prev) => ({ ...prev, photoUrl: downloadURL }));
    setCameraOpen(false);
    setSnackbar({
      open: true,
      message: "Photo Captured Successful.",
      severity: "success",
    });
  };

  const validateForm = () => {
    if (!formData.visitorName.trim()) return setError("Visitor name is required"), false;
    if (!formData.visitorEmail.trim()) return setError("Visitor email is required"), false;
    if (!formData.visitorMobileNo.trim()) return setError("Visitor mobile number is required"), false;
    if (!/^[0-9]{10,12}$/.test(formData.visitorMobileNo)) return setError("Enter valid 10-12 digit mobile number"), false;
    if (!formData.visitReason.trim()) return setError("Visit reason is required"), false;
    if (!formData.tenantId) return setError("Please select an organization"), false;
    if (!selectedFacility) return setError("Please select a facility"), false;
    const selectedFacilityData = facilities.find(f => f.id === selectedFacility);

    if (
      user?.role === "appoperator" &&
      selectedFacilityData?.facilityType === 100 &&
      !formData.hostUid
    )
      return setError("Please select a host/member"), false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;
    // If no photo → show warning popup
    if (!formData.photoUrl && !pendingSubmit) {
      setPhotoWarningOpen(true);
      return;
    }

    try {
      setLoading(true);
      const isOperator = user?.role === "appoperator";
      const selectedFacilityData = facilities.find(f => f.id === selectedFacility);
      const isResidential = selectedFacilityData?.facilityType === 100;

      const payload = {
        ...formData,
        tenantId: formData.tenantId,
        facilityId: selectedFacility,
        facilityType: selectedFacilityData?.facilityType,
        status: isResidential ? "pending" : (isOperator ? "approved" : "pending"),
      };

      // Remove host for non-residential / non-operator
      if (!(user?.role === "appoperator" && selectedFacilityData?.facilityType === 100)) {
        delete payload.hostUid;
      }

      const visitData = await submitVisitorRegistration(
        payload,
        user?.uid,
        user?.role,
        user?.email
      );

      setSnackbar({
        open: true,
        message: "Visit approval request submitted successfully!",
        severity: "success",
      });

      const redirectUrl = isOperator
        ? `/visitor-select-checkout?visitorEmail=${encodeURIComponent(formData.visitorEmail)}`
        : "/visit-approvals";

      setTimeout(() => navigate(redirectUrl), 1500);

      setFormData({
        visitorName: "",
        visitorEmail: "",
        visitorMobileNo: "",
        visitReason: "",
        tenantId: "",
        hostUid: "",
        visitorTime: new Date(),
        photoUrl: "",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to submit visit: ${err.message}`,
        severity: "error",
      });
      setError(`Registration failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHosts = async (facilityId) => {
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "host"),
        where("facilityId", "==", facilityId)
      );

      const snapshot = await getDocs(q);

      const hostsData = snapshot.docs.map(doc => ({
        id: doc.id,
        uid: doc.id,
        ...doc.data()
      }));

      setHosts(hostsData);
    } catch (error) {
      console.error("Failed to load hosts", error);
      setError("Failed to load hosts");
    }
  };

  const loadFacilities = async (tenantId) => {
    try {
      if (!tenantId) return;

      const facilitiesData = await getFacilitiesByTenant(tenantId);

      const formatted = facilitiesData.map((f) => ({
        id: f.id,
        name: f.fullName || f.facilityName || f.shortName || "Unknown",
        facilityType: f.facilityType,
        ...f
      }));

      setFacilities(formatted);

    } catch (error) {
      console.error("Error loading facilities:", error);
      setError("Failed to load facilities");
    }
  };

  const handleContinueWithoutPhoto = () => {
    setPhotoWarningOpen(false);
    setPendingSubmit(true);

    // Trigger submit again
    setTimeout(() => {
      document.querySelector("form").requestSubmit();
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "tenantId") {
      loadFacilities(value);
      loadHosts(value);
    }
  };


  useEffect(() => {
    if (user?.role === "appoperator" && user?.tenantId) {
      setFormData((prev) => ({
        ...prev,
        tenantId: user.tenantId
      }));
      loadFacilities(user.tenantId);
    }
  }, [user]);

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
      <Container maxWidth="sm">

        <Paper sx={{
          p: 4,
          borderRadius: "18px",
          border: "2px solid #f0d060",
          background: "#ffffff"
        }}>

          {/* HEADER */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography sx={{
              fontSize: "1.8rem",
              fontWeight: 900,
              fontFamily: "'Unbounded', sans-serif",
              color: "#1c1400"
            }}>
              Visitor Registration
            </Typography>

            <Typography sx={{ color: "#8a7a40" }}>
              Fill details to register a visitor
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>

            {/* PHOTO SECTION */}
            <Paper sx={{
              textAlign: "center",
              p: 3,
              mb: 3,
              borderRadius: "14px",
              background: "#fffbea",
              border: "1px solid #f0d060"
            }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                📷 Visitor Photo
              </Typography>

              <PhotoThumbnail
                photoUrl={formData.photoUrl}
                visitorName={formData.visitorName || "Visitor"}
                size={100}
                variant="circular"
                showZoom
                showCamera
                onPhotoCapture={() => setCameraOpen(true)}
              />

              <Typography sx={{ fontSize: "0.85rem", color: "#6b7280", mt: 1 }}>
                {formData.photoUrl
                  ? "Photo uploaded successfully"
                  : "Click camera icon to capture photo"}
              </Typography>
            </Paper>

            {/* INPUTS */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

              <TextField
                fullWidth
                label="Visitor Name"
                name="visitorName"
                value={formData.visitorName}
                onChange={handleChange}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <TextField
                fullWidth
                label="Visitor Email"
                name="visitorEmail"
                type="email"
                value={formData.visitorEmail}
                onChange={handleChange}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <TextField
                fullWidth
                label="Visitor Mobile Number"
                name="visitorMobileNo"
                type="tel"
                value={formData.visitorMobileNo}
                onChange={handleChange}
                required
                placeholder="1234567890"
                helperText="Enter 10–12 digit number"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <TextField
                fullWidth
                label="Visit Reason"
                name="visitReason"
                value={formData.visitReason}
                onChange={handleChange}
                multiline
                rows={2}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              {/* ORGANIZATION */}
              {user?.role !== "appoperator" && (
                <FormControl fullWidth>
                  <InputLabel>Select Organization</InputLabel>
                  <Select
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleChange}
                    required
                    sx={{ borderRadius: "10px" }}
                  >
                    {tenants
                      .filter((tenant) =>
                        user?.role === "visitor"
                          ? tenant.tenantType === 200 || tenant.tenantType === 300
                          : true
                      )
                      .map((tenant) => (
                        <MenuItem key={tenant.tenantId || tenant.id} value={tenant.tenantId || tenant.id}>
                          {tenant.tenantName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {/* FACILITY */}
              {(user?.role === "appoperator" || formData.tenantId) && (
                <FormControl fullWidth>
                  <InputLabel>Select Facility</InputLabel>

                  <Select
                    value={selectedFacility}
                    onChange={(e) => {
                      setSelectedFacility(e.target.value);
                      loadHosts(e.target.value);
                    }}
                    required
                    sx={{ borderRadius: "10px" }}
                  >
                    {facilities
                      .filter((facility) => {
                        if (user?.role !== "visitor") return true;
                        if (selectedTenant?.tenantType === 300) {
                          return facility.facilityType === 200;
                        }
                        return true;
                      })
                      .map((facility) => (
                        <MenuItem key={facility.id} value={facility.id}>
                          {facility.fullName || facility.facilityName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {/* HOST / MEMBER */}
              {user?.role === "appoperator" &&
                facilities.find(f => f.id === selectedFacility)?.facilityType === 100 && (
                  <FormControl fullWidth>
                    <InputLabel>Select Member</InputLabel>
                    <Select
                      name="hostUid"
                      value={formData.hostUid}
                      onChange={handleChange}
                      required
                      sx={{ borderRadius: "10px" }}
                    >
                      {hosts.map((host) => (
                        <MenuItem key={host.uid} value={host.uid}>
                          {host.userName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

              {/* DATE */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Visit Date & Time"
                  value={formData.visitorTime}
                  onChange={handleDateChange}
                  slots={{
                    textField: (params) => (
                      <TextField {...params} fullWidth required sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" }
                      }} />
                    ),
                  }}
                  minDate={new Date()}
                />
              </LocalizationProvider>

            </Box>

            {/* SUBMIT */}
            <Button
              type="submit"
              disabled={
                loading ||
                (facilities.find(f => f.id === selectedFacility)?.facilityType === 100 &&
                  user?.role === "visitor")
              }
              fullWidth
              sx={{
                mt: 3,
                py: 1.3,
                fontWeight: 700,
                borderRadius: "10px",
                background: "#1c1400",
                color: "#fde047",
                "&:hover": {
                  background: "#f59e0b",
                  color: "#1c1400"
                }
              }}
            >
              {loading ? "Submitting..." : "Submit Visit Request"}
            </Button>

            {/* ERROR */}
            {error && (
              <Typography sx={{ color: "#dc2626", mt: 2, textAlign: "center" }}>
                {error}
              </Typography>
            )}

          </form>
        </Paper>

        {/* DIALOG */}
        <Dialog open={photoWarningOpen} onClose={() => setPhotoWarningOpen(false)}>
          <DialogTitle>Photo Missing</DialogTitle>
          <DialogContent>
            <Typography>
              You have not uploaded a visitor photo.
              <br />
              Continue without photo?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPhotoWarningOpen(false)}>Upload Photo</Button>
            <Button variant="contained" onClick={handleContinueWithoutPhoto}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* CAMERA */}
        <CameraCapture
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onPhotoCapture={handlePhotoCapture}
          visitorName={formData.visitorName || "Visitor"}
        />

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default VisitorRegistration;
